import type { Offer, OfferSettings, OfferType } from '@makemydeal/dr-platform-types';
import type { PenciledInputsState, PartialPenciledInputsWithAllTypes } from '../../types/commonTypes';
import { initialState } from '../initialState';
import * as selectors from '../../selectors';
import { handleInitCases } from './handlers/initHandlers';
import { handleSyncAndResetCases } from './handlers/syncAndRestHandler';
import { handleIncentiveCases } from './handlers/incentiveHandlers';
import {
    isProgramIncentivesRestorationEnabled,
    isCashDealIncentivesFixesEnabled,
    isUpqRebateLedgerInEffect
} from '../../utils/incentives';
import { handlePaymentCases } from './handlers/paymentHandlers';
import { handlePriceUpdateCases } from './handlers/priceUpdateHandlers';
import { handleTaxAndFeeCases } from './handlers/taxAndFeeHandlers';
import { handleRateCases } from './handlers/rateHandlers';
import { handleResidualCases } from './handlers/residualHandlers';
import { handleBalloonCases } from './handlers/balloonHandlers';
import { handleOverrideCases } from './handlers/overrideHandlers';
import { handleCreditDecisionCases } from './handlers/creditDecisionHandlers';
import { handleVehicleAndTradeCases } from './handlers/vehicleAndTradeHandlers';
import { handlePaymentGridCases } from './handlers/paymentGridHandlers';
import {
    BALLOON_ACTIONS,
    CREDIT_DECISION_ACTIONS,
    INCENTIVE_ACTIONS,
    INIT_ACTIONS,
    OVERRIDE_ACTIONS,
    PAYMENT_ACTIONS,
    PAYMENT_GRID_ACTIONS,
    PRICE_UPDATE_ACTIONS,
    RATE_ACTIONS,
    RESIDUAL_ACTIONS,
    SYNC_RESET_ACTIONS,
    TAX_FEE_ACTIONS,
    VEHICLE_TRADE_ACTIONS
} from './constants';

export const buildUpdatedPenciledInputsState = (
    state: PenciledInputsState | undefined = initialState.penciledInputs,
    action: any,
    offerType: OfferType,
    offer: Offer,
    settings: OfferSettings
): PartialPenciledInputsWithAllTypes => {
    if (!state) return state as unknown as PartialPenciledInputsWithAllTypes;
    const isUserProgramQuotes = selectors.getIsUserProgramQuotes({ offer });

    if (INIT_ACTIONS.has(action.type)) {
        return handleInitCases(state, action, offerType, offer);
    }
    if (SYNC_RESET_ACTIONS.has(action.type)) {
        return handleSyncAndResetCases(state, action, offerType, offer);
    }
    if (INCENTIVE_ACTIONS.has(action.type)) {
        return handleIncentiveCases(
            state,
            action,
            offerType,
            isProgramIncentivesRestorationEnabled(offer),
            isCashDealIncentivesFixesEnabled(offer),
            // The toggle alone, deliberately not `&& isUserProgramQuotes`: incentive edits are moved onto
            // /program-quotes and back (`forceCalcEndpointToPqForIncentiveEdit`), so gating retention on the
            // endpoint would strip the frozen set during the very edits it has to survive. The endpoint
            // decides whether the set is READ, not whether it survives.
            isUpqRebateLedgerInEffect(offer, offerType)
        );
    }
    if (PAYMENT_ACTIONS.has(action.type)) {
        return handlePaymentCases(state, action, offerType, offer, settings, isUserProgramQuotes);
    }
    if (PRICE_UPDATE_ACTIONS.has(action.type)) {
        return handlePriceUpdateCases(state, action, offerType);
    }
    if (TAX_FEE_ACTIONS.has(action.type)) {
        return handleTaxAndFeeCases(state, action, offerType);
    }
    if (RATE_ACTIONS.has(action.type)) {
        return handleRateCases(state, action, offerType, isUserProgramQuotes, selectors.isAspPaymentGridRedesignEnabled({ offer }));
    }
    if (RESIDUAL_ACTIONS.has(action.type)) {
        return handleResidualCases(
            state,
            action,
            offerType,
            offer,
            isUserProgramQuotes,
            selectors.isAspPaymentGridRedesignEnabled({ offer })
        );
    }
    if (BALLOON_ACTIONS.has(action.type)) {
        return handleBalloonCases(state, action, offerType, offer, isUserProgramQuotes);
    }
    if (OVERRIDE_ACTIONS.has(action.type)) {
        return handleOverrideCases(
            state,
            action,
            offerType,
            offer,
            isUserProgramQuotes,
            selectors.isAspPaymentGridRedesignEnabled({ offer })
        );
    }
    if (CREDIT_DECISION_ACTIONS.has(action.type)) {
        return handleCreditDecisionCases(state, action, offerType, offer, isUserProgramQuotes);
    }
    if (VEHICLE_TRADE_ACTIONS.has(action.type)) {
        return handleVehicleAndTradeCases(
            state,
            action,
            offerType,
            offer,
            isUserProgramQuotes,
            selectors.isAspPaymentGridRedesignEnabled({ offer })
        );
    }
    if (PAYMENT_GRID_ACTIONS.has(action.type)) {
        return handlePaymentGridCases(
            state,
            action,
            offerType,
            offer,
            isUserProgramQuotes,
            selectors.isAspPaymentGridRedesignEnabled({ offer })
        );
    }

    return state;
};
